import { z } from 'zod';
/**
 * Industry review workflows (tracker task 80, decision 0060). An industry profile can add steps before
 * cover is decided:
 * - a specialist review, when a control the specialist watches is not fully in place (an OT specialist
 *   for an energy firm's control network);
 * - evidence before acceptance: named controls must rest on more than the business's word (a document,
 *   a scan or an integration) before cover is accepted;
 * - a second sign-off: one underwriter proposes the decision and another confirms it (financial services).
 * A referral needs none of them: it hands the assessment on.
 */
export const ProfileReview = z.strictObject({
    /** Underwriters who sign a cover decision. 2: a second, different underwriter confirms the first's. */
    sign_offs: z.number().int().min(1).max(2).default(1),
    /** Reviews before anyone decides, when one of its controls is not fully in place. */
    specialist: z
        .strictObject({
        /** Who reviews, as the underwriter reads it: "OT specialist", "Clinical systems lead". */
        label: z.string().trim().min(1).max(80),
        controls: z.array(z.string().min(1)).min(1).max(50),
    })
        .optional(),
    /** Controls that need evidence beyond the business's word before cover is accepted. */
    evidence_before_accept: z.array(z.string().min(1)).max(50).default([]),
});
export const SPECIALIST_OUTCOMES = ['no_concerns', 'concerns'];
export const SPECIALIST_OUTCOME_LABEL = {
    no_concerns: 'No concerns',
    concerns: 'Concerns, for the underwriter to weigh',
};
export const CreateSpecialistReview = z.strictObject({
    outcome: z.enum(SPECIALIST_OUTCOMES),
    note: z.string().trim().min(1).max(2000),
});
/** The second underwriter's answer to a proposed decision. */
export const AnswerProposal = z
    .strictObject({
    action: z.enum(['confirm', 'return']),
    note: z.string().trim().min(1).max(2000).optional(),
})
    .superRefine((a, ctx) => {
    if (a.action === 'return' && !a.note)
        ctx.addIssue({ code: 'custom', path: ['note'], message: 'Say why it goes back' });
});
/**
 * Evidence beyond the business's word: a document read, a scan, an integration, a checked claim. Self-declared
 * answers count 0.2 (scoring's EVIDENCE_CONFIDENCE, not imported: scoring reads profiles, which read this).
 */
export const evidenced = (confidence) => confidence > 0.2;
/**
 * The steps an assessment's review takes, from its profile and score. `reviews` are the specialist reviews
 * recorded, newest first; one counts while it covers every watched control now not fully in place, so a
 * newly weak answer asks for another.
 */
export function reviewSteps(inp) {
    const r = inp.profile?.review;
    const scores = new Map((inp.score?.control_scores ?? []).map((c) => [c.control, c]));
    const weak = (c) => {
        const s = scores.get(c);
        return !!s && s.implementation !== null && s.implementation < 1;
    };
    return {
        profile: inp.profile?.name ?? null,
        sign_offs: r?.sign_offs ?? 1,
        specialist: r?.specialist
            ? (() => {
                const because = r.specialist.controls.filter(weak);
                const review = inp.reviews.find((x) => because.every((c) => x.controls.includes(c))) ?? null;
                return { label: r.specialist.label, because, review };
            })()
            : null,
        evidence: (r?.evidence_before_accept ?? [])
            .filter((c) => scores.has(c))
            .map((c) => ({ control: c, evidenced: evidenced(scores.get(c).confidence) })),
        proposal: inp.proposal,
    };
}
/** Why this outcome can't be recorded yet, or null. A referral is never held up. */
export function decisionBlockedBy(steps, outcome) {
    if (outcome === 'refer')
        return null;
    if (steps.proposal)
        return 'A decision is already waiting for a second underwriter';
    const s = steps.specialist;
    if (s && s.because.length && !s.review)
        return `${s.label} review first: ${s.because.join(', ')} ${s.because.length === 1 ? 'is' : 'are'} not fully in place`;
    const missing = steps.evidence.filter((e) => !e.evidenced).map((e) => e.control);
    if (outcome !== 'decline' && missing.length)
        return `Evidence first: ${missing.join(', ')} ${missing.length === 1 ? 'rests' : 'rest'} on the business’s word alone`;
    return null;
}
//# sourceMappingURL=review-steps.js.map